// @givmo/app-admin — the sent-gift-card STATUS cell (EP-WEB4 / EP-FIX5 / GC-FU2).
//
// ONE small dedicated component that renders a company gift card's DELIVERY status: Pending →
// Delivered → Failed, plus the terminal Voided / tolerant fallback.
// ★ The wire's `delivery_status` is the authority whenever the card carries one, and since be#2223
// (2026-09-24) every new send does. A BATCH member gets it from the async worker (PENDING →
// SENT/FAILED, retried). A SYNCHRONOUS single send gets it right after its email attempt: SENT when
// the provider accepted the email, FAILED when it refused or the attempt errored — a best-effort
// marker written after the money moved, so a failed marker write never fails the send. SENT always
// carries `delivered_at`, because both writers set the two together. A card with NEITHER field is a
// synchronous send from before be#2223 (nothing was backfilled), or one whose dispatch or marker
// write failed before anything was recorded: it was dispatched when it was sent and the record says
// no more, so bare ACTIVE reads "Delivered". Keying delivery off `delivered_at` alone is the trap: it
// strands every such card on "Pending" forever and makes a FAILED card indistinguishable from a
// pending one.
// ★ ALL redemption rendering lives HERE, behind the `SHOW_REDEMPTION` switch, which is OFF per
// D-EP-17 — so a REDEEMED card collapses to "Delivered" everywhere the sent-list shows it (both the
// Sent tab and the batch report's per-card rows, which reuse this same cell).

import { Badge } from '@givmo/design-system';
import { toDateOnly } from '@givmo/api-client/list';
import type { CompanyGiftCardOut } from '@givmo/api-client';

/**
 * Whether to reveal the REDEEMED state to the sending company. **FALSE per D-EP-17 (2026-07-26):**
 * the employer sees delivery only — whether and when an employee redeemed a gift is theirs, not the
 * company's (employee-privacy posture). A REDEEMED card therefore renders "Delivered" (a redeemed
 * card was necessarily delivered) and its `redeemed_at` never reaches the DOM. Kept as a switch so
 * the policy is one line to revisit; do not re-expose redemption without a superseding decision.
 */
const SHOW_REDEMPTION = false;

/** "Delivered" (+ the delivery date when present). The shared terminal-delivered rendering. */
function DeliveredBadge({ card }: { card: CompanyGiftCardOut }) {
  const when = toDateOnly(card.delivered_at);
  return (
    <Badge tone="info" dot testId={`gift-card-status-${card.id}`}>
      {when.length > 0 ? `Delivered ${when}` : 'Delivered'}
    </Badge>
  );
}

export function GiftCardStatusCell({ card }: { card: CompanyGiftCardOut }) {
  // ── REDEEMED: the sole redemption rendering, currently gated OFF (D-EP-17) → "Delivered". ──
  if (card.status === 'REDEEMED') {
    if (!SHOW_REDEMPTION) return <DeliveredBadge card={card} />;
    const when = toDateOnly(card.redeemed_at);
    return (
      <Badge tone="success" dot testId={`gift-card-status-${card.id}`}>
        {when.length > 0 ? `Redeemed ${when}` : 'Redeemed'}
      </Badge>
    );
  }

  if (card.status === 'VOIDED') {
    return (
      <Badge tone="neutral" dot testId={`gift-card-status-${card.id}`}>
        Voided
      </Badge>
    );
  }

  // ── The wire's own delivery state, when the card carries one (every send since be#2223). ──
  const delivery = card.delivery_status?.trim();
  if (delivery) {
    // SENT always arrives with `delivered_at` (the async worker and the synchronous marker each set
    // both in one write).
    if (delivery === 'SENT') return <DeliveredBadge card={card} />;
    if (delivery === 'FAILED') {
      return (
        <Badge tone="danger" dot testId={`gift-card-status-${card.id}`}>
          Failed
        </Badge>
      );
    }
    // PENDING — and any unknown future value: a tolerant reader degrades to the pre-terminal
    // label rather than leaking a raw enum into the DOM.
    return (
      <Badge tone="warning" dot testId={`gift-card-status-${card.id}`}>
        Pending
      </Badge>
    );
  }

  // ACTIVE with no wire delivery state = a synchronous send from before be#2223, or one whose
  // dispatch or marker write recorded nothing (see the header): there is no pending phase to report,
  // and `delivered_at` is absent, so this reads bare "Delivered".
  if (card.status === 'ACTIVE') return <DeliveredBadge card={card} />;

  // INACTIVE / any unknown wire value — tolerant fallback (never throws on an unexpected status).
  return (
    <Badge tone="neutral" dot testId={`gift-card-status-${card.id}`}>
      Pending
    </Badge>
  );
}
